const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const SIZE_SORT_ORDER = {
  xxs: 0,
  "extra extra small": 0,
  xs: 1,
  "extra small": 1,
  s: 2,
  small: 2,
  m: 3,
  medium: 3,
  l: 4,
  large: 4,
  xl: 5,
  "extra large": 5,
  xxl: 6,
  "2xl": 6,
  "extra extra large": 6,
  xxxl: 7,
  "3xl": 7,
};

const getPrimaryVariantAttribute = (variant) => {
  const attributes = Array.isArray(variant?.variantAttributes)
    ? variant.variantAttributes
    : [];
  return attributes.length > 0 ? attributes[0] : null;
};

const getVariantDisplayLabel = (variant) => {
  const primary = getPrimaryVariantAttribute(variant);
  return String(
    primary?.label || primary?.value || variant?.variantLabel || variant?.variantKey || "Default",
  ).trim();
};

const isSizeDimension = (dimensionKey) => {
  const key = normalizeText(dimensionKey);
  return key === "size" || key.includes("size");
};

const isVolumeDimension = (dimensionKey) => {
  const key = normalizeText(dimensionKey);
  return key === "volume" || key.includes("volume");
};

const parseVolumeLabel = (label) => {
  const normalized = normalizeText(label);
  const match = normalized.match(/(\d+(?:\.\d+)?)\s*(ml|l|oz|g|kg)?/i);
  if (!match) {
    return { value: Number.POSITIVE_INFINITY, unit: normalized };
  }

  const amount = Number(match[1]);
  const unit = normalizeText(match[2] || "");

  if (!Number.isFinite(amount)) {
    return { value: Number.POSITIVE_INFINITY, unit };
  }

  if (unit === "l") {
    return { value: amount * 1000, unit: "ml" };
  }

  if (unit === "kg") {
    return { value: amount * 1000, unit: "g" };
  }

  return { value: amount, unit };
};

const getVariantSortSignature = (variant) => {
  if (!variant || typeof variant !== "object") return "";

  if (Array.isArray(variant.variantAttributes) && variant.variantAttributes.length > 0) {
    return variant.variantAttributes
      .slice()
      .sort((left, right) => {
        const leftPos = Number.isFinite(left?.position)
          ? left.position
          : Number.MAX_SAFE_INTEGER;
        const rightPos = Number.isFinite(right?.position)
          ? right.position
          : Number.MAX_SAFE_INTEGER;
        return leftPos - rightPos;
      })
      .map((attr) => String(attr?.label || attr?.value || "").trim().toLowerCase())
      .filter(Boolean)
      .join("|");
  }

  return String(variant.variantLabel || variant.variantKey || "")
    .trim()
    .toLowerCase();
};

const compareVariantsAscending = (left, right) => {
  const leftPrimary = getPrimaryVariantAttribute(left);
  const rightPrimary = getPrimaryVariantAttribute(right);

  const leftDimensionKey = normalizeText(
    leftPrimary?.dimensionKey || leftPrimary?.dimensionName,
  );
  const rightDimensionKey = normalizeText(
    rightPrimary?.dimensionKey || rightPrimary?.dimensionName,
  );

  const leftLabel = getVariantDisplayLabel(left);
  const rightLabel = getVariantDisplayLabel(right);

  const leftDimensionType = isSizeDimension(leftDimensionKey)
    ? "size"
    : isVolumeDimension(leftDimensionKey)
      ? "volume"
      : "other";

  const rightDimensionType = isSizeDimension(rightDimensionKey)
    ? "size"
    : isVolumeDimension(rightDimensionKey)
      ? "volume"
      : "other";

  if (leftDimensionType === "size" && rightDimensionType === "size") {
    const leftSizeOrder = SIZE_SORT_ORDER[normalizeText(leftLabel)] ?? 999;
    const rightSizeOrder = SIZE_SORT_ORDER[normalizeText(rightLabel)] ?? 999;
    if (leftSizeOrder !== rightSizeOrder) {
      return leftSizeOrder - rightSizeOrder;
    }
    return leftLabel.localeCompare(rightLabel, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  }

  if (leftDimensionType === "volume" && rightDimensionType === "volume") {
    const leftVolume = parseVolumeLabel(leftLabel);
    const rightVolume = parseVolumeLabel(rightLabel);
    if (leftVolume.value !== rightVolume.value) {
      return leftVolume.value - rightVolume.value;
    }
    return leftLabel.localeCompare(rightLabel, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  }

  const leftSignature = getVariantSortSignature(left);
  const rightSignature = getVariantSortSignature(right);
  const signatureCompare = leftSignature.localeCompare(rightSignature, undefined, {
    sensitivity: "base",
    numeric: true,
  });

  if (signatureCompare !== 0) {
    return signatureCompare;
  }

  return leftLabel.localeCompare(rightLabel, undefined, {
    sensitivity: "base",
    numeric: true,
  });
};

const sortVariantsAscending = (variants = []) => [...variants].sort(compareVariantsAscending);

export {
  compareVariantsAscending,
  getVariantDisplayLabel,
  sortVariantsAscending,
};